import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { getClassNames, resetState, getPreference } from '../../../utils/util';
import { getMenuItems, getSettingsMenuItems } from '../../../utils/side-menu.util';

import { resetProductManagementSlice } from '../../../slices/admin/product-management/product-management.slice';
import { resetDealRoomManagementSlice } from '../../../slices/admin/deal-room-management/deal-room-management.slice';
import { resetAccountManagementSlice } from '../../../slices/admin/account-management/account-management.slice';
import { setCurrencyCode } from '../../../slices/currency/currency.slice';

import Image from '../image/Image';
import Tooltip from '../tooltip/ToolTip';
import CurrencyModal from './currency-modal/CurrencyModal';

import { ReactComponent as LogoDark } from '../../../resources/logos/atp-logo-dark.svg';
import { ReactComponent as SettingsIcon } from '../../../resources/icons/settings.svg';
import { ReactComponent as ProfileIcon } from '../../../resources/icons/profile.svg';

import './side-menu.scss';

const SideMenu = ({ profile, adminPings }) => {
  const userGroups = useSelector(({ party }) => party.userGroups);
  const currencyCode = useSelector(({ currency }) => currency.currencyCode);

  const [openSection, setOpenSection] = useState('Dashboard');
  const [activeItem, setActiveItem] = useState('dashboard');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [displayTooltipName, setDisplayTooltipName] = useState('');
  const [displayPingName, setDisplayPingName] = useState('');
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const currencyCode = getPreference('currency');
    if (currencyCode) {
      dispatch(setCurrencyCode(currencyCode));
    }
  }, [dispatch]);

  useEffect(() => {
    const { pathname } = location;
    const basePath = pathname.split('/')[1];

    if (basePath === 'shop') {
      setOpenSection('My Shop');
    } else if (basePath === 'market') {
      setOpenSection('The Market');
    } else if (basePath === 'dashboard') {
      setOpenSection('Dashboard');
    } else if (basePath === 'admin') {
      if (userGroups.includes('facilitator')) {
        setOpenSection('Facilitator');
      } else if (userGroups.includes('admin')) {
        setOpenSection('Admin');
      }
    } else if (basePath === 'reports') {
      setOpenSection('Reports');
    } else {
      setOpenSection(undefined);
    }

    setActiveItem(pathname);
  }, [location, userGroups]);

  const handleMenuItemClick = (item, active) => {
    if (active) return;

    const { pathname } = location;
    if (pathname.includes('product-management')) {
      dispatch(resetProductManagementSlice());
    } else if (pathname.includes('deal-room-management')) {
      dispatch(resetDealRoomManagementSlice());
    } else if (pathname.includes('account-management')) {
      dispatch(resetAccountManagementSlice());
    }

    if (item.path) {
      navigate(item.path);
    } else {
      setOpenSection(item.name);
      const filteredItems = item.subItems.filter((subItem) => !subItem.hidden);
      navigate(filteredItems[0].path);
    }
    setSettingsOpen(false);
  };

  const handleCurrencyModalClose = (currencyCode) => {
    if (currencyCode) {
      dispatch(setCurrencyCode(currencyCode));
    }

    setShowCurrencyModal(false);
  };

  const renderMenuSubItems = (name, subItems) => {
    return (
      <div className={getClassNames('side-menu__sub-items', { open: name === openSection })}>
        {subItems.map((subItem, subIndex) => {
          if (subItem.hidden) return null;

          const active = activeItem.includes(subItem.path);

          return (
            <div
              className="side-menu__item-container"
              key={subIndex}
              onMouseEnter={() => {
                setDisplayTooltipName(subItem.name);
                setDisplayPingName(subItem.name);
              }}
              onMouseLeave={() => {
                setDisplayTooltipName('');
                setDisplayPingName('');
              }}
            >
              <button
                className={getClassNames('side-menu__item', {
                  sub: true,
                  'active-sub': active,
                })}
                onClick={() => handleMenuItemClick(subItem, active)}
                key={subIndex}
              >
                {subItem.name}
              </button>
              {subItem.tooltipText && displayTooltipName === subItem.name && (
                <div className="side-menu__tooltip">
                  <Tooltip text={subItem.tooltipText} maxWidth={400} toolTipLeft={false} />
                </div>
              )}
              {subItem.adminNotifications && displayPingName === subItem.name && (
                <div className="side-menu__circle-container">
                  <div className="side-menu__circle">
                    <p>{subItem.pings ? subItem.pings : 0}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMenuItems = () => {
    const menuItems = getMenuItems(userGroups, adminPings);
    return menuItems.map((item, index) => {
      if (item.hidden) return null;

      const { name, subItems } = item;
      const active = openSection === name;

      return (
        <section key={index}>
          <button
            className={getClassNames('side-menu__item', { active })}
            onClick={() => handleMenuItemClick(item, active)}
          >
            {name}
          </button>
          {renderMenuSubItems(name, subItems)}
        </section>
      );
    });
  };

  const handleSettingsMenuItemClick = (item) => {
    if (item.name === 'Sign Out') {
      resetState(dispatch);
    }

    navigate(item.path);
  };

  const renderSettingsMenuItems = () => {
    return getSettingsMenuItems().map((item, index) => {
      if (item.hidden) return null;

      let active = false;
      if (item.path) {
        active = activeItem.includes(item.path) && item.path !== '/';
      }

      return (
        <button
          className={getClassNames('side-menu__settings-menu__item', { active })}
          key={index}
          onClick={() => handleSettingsMenuItemClick(item)}
        >
          {item.name}
        </button>
      );
    });
  };

  return (
    <>
      <section className="side-menu">
        <section className="side-menu__logo">
          <LogoDark />
        </section>
        <section className="side-menu__items">{renderMenuItems()}</section>
        <div className={getClassNames('side-menu__settings-menu', { open: settingsOpen })}>
          {renderSettingsMenuItems()}
        </div>
        <section className="side-menu__settings">
          <div className="side-menu__user">
            {profile.profilePictureExternalReference ? (
              <Image
                key={profile.profilePictureExternalReference}
                className="side-menu__user__image"
                externalReference={profile.profilePictureExternalReference}
                alt="profile"
              />
            ) : (
              <ProfileIcon />
            )}
            <div>
              <p>{profile.companyName}</p>
              <p>{profile.username}</p>
            </div>
          </div>
          <section className="side-menu__settings__buttons">
            <button className="side-menu__currency" onClick={() => setShowCurrencyModal(true)}>
              {currencyCode || 'USD'}
            </button>
            <SettingsIcon onClick={() => setSettingsOpen(!settingsOpen)} data-testid="settings-icon" />
          </section>
        </section>
      </section>

      <CurrencyModal show={showCurrencyModal} onClose={handleCurrencyModalClose} />
    </>
  );
};

SideMenu.propTypes = {
  profile: PropTypes.shape({}),
  adminPings: PropTypes.shape({}),
};

SideMenu.defaultProps = {
  profile: {},
  adminPings: {},
};

export default SideMenu;
